import { Component, ElementRef, Injector, NgZone, ViewChild, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { MarketingEventEnumeration, MarketingEventService, RecurrentStatesEnumeration } from '@shared/services';

// Enum
import { PRODUCT_ID_ENUMERATION, ProductType } from '@core/model/enums';

// Configs
import { ROUTES_CONSTANTS } from '@shared/configs';

import {
  CHECK_PROCESS_STATE_ENUMERATION,
  checkProcessRedirect,
  CheckProcessResponseType,
  CheckProcessService,
} from '@core/service/check-process';

@Component({
  selector: 'app-references',
  templateUrl: './references.component.html',
})
export class ReferencesComponent implements OnInit {
  @ViewChild('referencesRef') public referencesRef!: ElementRef;

  private readonly webElementService: WebcomponentsService;
  private readonly ngZone: NgZone;
  private readonly router: Router;
  private readonly authService: AuthService;
  private readonly marketingEventService: MarketingEventService;
  private readonly checkProcessService: CheckProcessService;
  public clientType: RecurrentStatesEnumeration;
  public productId: number;
  private unSubscribe$: Subject<void>;

  public isLoading: boolean;

  constructor(private readonly injector: Injector) {
    this.webElementService = this.injector.get(WebcomponentsService);
    this.ngZone = this.injector.get(NgZone);
    this.router = this.injector.get(Router);
    this.authService = this.injector.get(AuthService);
    this.marketingEventService = this.injector.get(MarketingEventService);
    this.checkProcessService = this.injector.get(CheckProcessService);
    this.clientType = RecurrentStatesEnumeration.IS_NEW;
    this.unSubscribe$ = new Subject<void>();
    this.isLoading = false;
  }

  public ngOnInit(): void {
    this.isLoading = true;
    this.checkProcessService
      .checkProcess()
      .pipe(
        takeUntil(this.unSubscribe$),
        tap(({ state, customerType, productType: productId }: CheckProcessResponseType) => {
          this.clientType = customerType as RecurrentStatesEnumeration;
          this.productId = productId;

          this.ngZone.run(() => {
            const route = checkProcessRedirect(state as CHECK_PROCESS_STATE_ENUMERATION, [
              CHECK_PROCESS_STATE_ENUMERATION.PENDING_REFERENCES,
            ]);

            if (route) {
              this.router.navigate([route]);
            }
          });
        }),
        switchMap(() => this.webElementService.isLoaded$.pipe(filter(Boolean), takeUntil(this.unSubscribe$)))
      )
      .subscribe({
        next: () => {
          this.renderReferencesWidget();
        },
        error: (err) => {
          console.error('Error cargando estrategia o WebComponent', err);
          this.router.navigate([ROUTES_CONSTANTS.HOME]);
        },
      });
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private renderReferencesWidget(): void {
    const references = this.referencesRef.nativeElement;

    references.ngElementStrategy?.componentRef?.instance?.build({
      email: this.authService.user.email,
      clientType: this.clientType,
      redirectToFn: this.onConfirm.bind(this),
    });

    this.isLoading = false;
  }

  public onConfirm(): void {
    this.ngZone.run(() => {
      let eventName: MarketingEventEnumeration;
      let productType = this.productId === Number(PRODUCT_ID_ENUMERATION.RP) ? ProductType.RP : ProductType.RFYA;

      if (productType === ProductType.RP) {
        eventName =
          this.clientType === RecurrentStatesEnumeration.IS_RECURRING
            ? MarketingEventEnumeration.RECURRENT_APPROVED_RP
            : MarketingEventEnumeration.NEW_APPROVED_RP;
      } else {
        eventName = MarketingEventEnumeration.LOAN_APPLICATION_SIGNATURE_RFYA;
      }

      this.marketingEventService.sendMarketingEvents(eventName);
      this.router.navigate([ROUTES_CONSTANTS.SIGN_APPLICATION]);
    });
  }
}
